import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  CreditCard, Save, Send, Plus, Trash2, Star, Edit2, X, Check,
  Loader2, Mail, DollarSign, Calendar, Building2, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import MobileNav from "../components/MobileNav";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  premium: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-400", label: "💎 Premium" },
  standard: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-400", label: "💳 Standard" },
  value: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400", label: "🎯 Value" },
  starter: { bg: "bg-yellow-500/10", border: "border-yellow-500/30", text: "text-yellow-400", label: "🌱 Starter" },
};

export default function TradelineManagerPage() {
  const [tradelines, setTradelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ client_name: "", client_email: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [newTradeline, setNewTradeline] = useState({
    name: "",
    opened_date: "",
    limit: "",
    posting_day: "",
    price: "",
    tier: "value",
    is_featured: false,
    is_active: true
  });

  useEffect(() => {
    loadTradelines();
  }, []);

  const loadTradelines = async () => {
    try {
      const response = await axios.get(`${API}/api/tradelines`);
      setTradelines(response.data.tradelines || []);
    } catch (error) {
      console.error("Error loading tradelines:", error);
      toast.error("Failed to load tradelines");
    } finally {
      setLoading(false);
    }
  };

  const saveTradelines = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/tradelines`, { tradelines });
      toast.success("Tradelines saved successfully!");
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving tradelines:", error);
      toast.error("Failed to save tradelines");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tradeline) => {
    setEditingId(tradeline.id);
    setEditForm({ ...tradeline });
  };

  const handleSaveEdit = () => {
    setTradelines(prev => prev.map(t => t.id === editingId ? { ...editForm } : t));
    setEditingId(null);
    setEditForm({});
    setHasChanges(true);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleDelete = (id) => {
    setTradelines(prev => prev.filter(t => t.id !== id));
    setHasChanges(true);
    toast.success("Tradeline removed");
  };

  const handleToggleActive = (id) => {
    setTradelines(prev => prev.map(t => 
      t.id === id ? { ...t, is_active: !t.is_active } : t
    ));
    setHasChanges(true);
  };

  const handleToggleFeatured = (id) => {
    setTradelines(prev => prev.map(t => 
      t.id === id ? { ...t, is_featured: !t.is_featured } : t
    ));
    setHasChanges(true);
  };

  const handleAddTradeline = () => {
    const newId = String(Date.now());
    setTradelines(prev => [...prev, { ...newTradeline, id: newId }]);
    setNewTradeline({
      name: "",
      opened_date: "",
      limit: "",
      posting_day: "",
      price: "",
      tier: "value",
      is_featured: false,
      is_active: true
    });
    setShowAddModal(false);
    setHasChanges(true);
    toast.success("Tradeline added!");
  };

  const handleSendEmail = async () => {
    if (!emailData.client_name || !emailData.client_email) {
      toast.error("Please enter client name and email");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/tradelines/send-email`, emailData);
      toast.success(`Tradeline list sent to ${emailData.client_email}!`);
      setShowEmailModal(false);
      setEmailData({ client_name: "", client_email: "" });
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Group tradelines by tier
  const groupedTradelines = tradelines.reduce((acc, t) => {
    const tier = t.tier || "value";
    if (!acc[tier]) acc[tier] = [];
    acc[tier].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <MobileNav />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-lg">
              <CreditCard className="w-6 h-6" />
            </div>
            Tradeline Manager
          </h1>
          <p className="text-zinc-400 mt-1">Manage your aged authorized user trade lines</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={() => setShowAddModal(true)}
            variant="outline"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
          <Button
            onClick={() => setShowEmailModal(true)}
            variant="outline"
            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send to Client
          </Button>
          <Button
            onClick={saveTradelines}
            disabled={saving || !hasChanges}
            className={`${hasChanges ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-700'}`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {hasChanges ? "Save Changes" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{tradelines.length}</p>
            <p className="text-xs text-zinc-500">Total</p>
          </CardContent>
        </Card>
        {Object.entries(TIER_COLORS).map(([tier, colors]) => (
          <Card key={tier} className={`${colors.bg} ${colors.border} border`}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${colors.text}`}>
                {groupedTradelines[tier]?.length || 0}
              </p>
              <p className="text-xs text-zinc-500">{colors.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tradeline Tables by Tier */}
      {["premium", "standard", "value", "starter"].map(tier => {
        const tierTradelines = groupedTradelines[tier] || [];
        const colors = TIER_COLORS[tier];
        
        if (tierTradelines.length === 0) return null;
        
        return (
          <Card key={tier} className={`bg-zinc-900 border ${colors.border}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-lg ${colors.text} flex items-center gap-2`}>
                {colors.label} ({tierTradelines.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700 text-zinc-400">
                      <th className="text-left py-3 px-2">Card Name</th>
                      <th className="text-left py-3 px-2">Opened</th>
                      <th className="text-left py-3 px-2">Limit</th>
                      <th className="text-left py-3 px-2">Posts</th>
                      <th className="text-left py-3 px-2">Price</th>
                      <th className="text-center py-3 px-2">Status</th>
                      <th className="text-right py-3 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tierTradelines.map((t, idx) => (
                      <tr 
                        key={t.id} 
                        className={`border-b border-zinc-800 ${!t.is_active ? 'opacity-50' : ''} ${idx % 2 === 0 ? colors.bg : ''}`}
                      >
                        {editingId === t.id ? (
                          // Edit Mode
                          <>
                            <td className="py-2 px-2">
                              <Input
                                value={editForm.name}
                                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                className="h-8 bg-zinc-800 border-zinc-600 text-sm"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                value={editForm.opened_date}
                                onChange={(e) => setEditForm({...editForm, opened_date: e.target.value})}
                                className="h-8 bg-zinc-800 border-zinc-600 text-sm w-28"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                value={editForm.limit}
                                onChange={(e) => setEditForm({...editForm, limit: e.target.value})}
                                className="h-8 bg-zinc-800 border-zinc-600 text-sm w-24"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                value={editForm.posting_day}
                                onChange={(e) => setEditForm({...editForm, posting_day: e.target.value})}
                                className="h-8 bg-zinc-800 border-zinc-600 text-sm w-16"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <Input
                                value={editForm.price}
                                onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                                className="h-8 bg-zinc-800 border-zinc-600 text-sm w-20"
                              />
                            </td>
                            <td className="py-2 px-2 text-center">
                              <Select
                                value={editForm.tier}
                                onValueChange={(val) => setEditForm({...editForm, tier: val})}
                              >
                                <SelectTrigger className="h-8 w-28 bg-zinc-800 border-zinc-600 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="premium">Premium</SelectItem>
                                  <SelectItem value="standard">Standard</SelectItem>
                                  <SelectItem value="value">Value</SelectItem>
                                  <SelectItem value="starter">Starter</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={handleSaveEdit} className="h-7 w-7 p-0 text-green-400">
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={handleCancelEdit} className="h-7 w-7 p-0 text-red-400">
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          // View Mode
                          <>
                            <td className="py-3 px-2 font-medium text-white">
                              {t.name}
                              {t.is_featured && <Star className="w-3 h-3 inline ml-1 text-yellow-400 fill-yellow-400" />}
                            </td>
                            <td className="py-3 px-2 text-zinc-400">{t.opened_date}</td>
                            <td className="py-3 px-2 text-green-400">{t.limit}</td>
                            <td className="py-3 px-2 text-yellow-400">📅 {t.posting_day}</td>
                            <td className={`py-3 px-2 font-bold ${colors.text}`}>{t.price}</td>
                            <td className="py-3 px-2 text-center">
                              <button
                                onClick={() => handleToggleActive(t.id)}
                                className={`px-2 py-1 rounded text-xs ${t.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                              >
                                {t.is_active ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleToggleFeatured(t.id)}
                                  className={`h-7 w-7 p-0 ${t.is_featured ? 'text-yellow-400' : 'text-zinc-500'}`}
                                >
                                  <Star className={`w-4 h-4 ${t.is_featured ? 'fill-yellow-400' : ''}`} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleEdit(t)} className="h-7 w-7 p-0 text-blue-400">
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="h-7 w-7 p-0 text-red-400">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Add Tradeline Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-cyan-400" />
              Add New Tradeline
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Card Name *</Label>
              <Input
                value={newTradeline.name}
                onChange={(e) => setNewTradeline({...newTradeline, name: e.target.value})}
                placeholder="WFBNA CARD"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Opened Date *</Label>
                <Input
                  value={newTradeline.opened_date}
                  onChange={(e) => setNewTradeline({...newTradeline, opened_date: e.target.value})}
                  placeholder="01-30-2019"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Limit *</Label>
                <Input
                  value={newTradeline.limit}
                  onChange={(e) => setNewTradeline({...newTradeline, limit: e.target.value})}
                  placeholder="$37,000"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Posting Day *</Label>
                <Input
                  value={newTradeline.posting_day}
                  onChange={(e) => setNewTradeline({...newTradeline, posting_day: e.target.value})}
                  placeholder="8th"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Price *</Label>
                <Input
                  value={newTradeline.price}
                  onChange={(e) => setNewTradeline({...newTradeline, price: e.target.value})}
                  placeholder="$600"
                  className="mt-1 bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Tier</Label>
              <Select
                value={newTradeline.tier}
                onValueChange={(val) => setNewTradeline({...newTradeline, tier: val})}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">💎 Premium ($550-$600)</SelectItem>
                  <SelectItem value="standard">💳 Standard ($420-$520)</SelectItem>
                  <SelectItem value="value">🎯 Value ($260-$400)</SelectItem>
                  <SelectItem value="starter">🌱 Starter ($250)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleAddTradeline}
              disabled={!newTradeline.name || !newTradeline.opened_date || !newTradeline.limit || !newTradeline.posting_day || !newTradeline.price}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tradeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-400" />
              Send Tradeline List to Client
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Send a beautifully designed tradeline list with all active tradelines, posting dates, and payment methods.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Client Name *</Label>
              <Input
                value={emailData.client_name}
                onChange={(e) => setEmailData({...emailData, client_name: e.target.value})}
                placeholder="John Smith"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Client Email *</Label>
              <Input
                type="email"
                value={emailData.client_email}
                onChange={(e) => setEmailData({...emailData, client_email: e.target.value})}
                placeholder="client@example.com"
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="bg-zinc-800 rounded-lg p-4">
              <p className="text-sm text-zinc-400 mb-2">Email will include:</p>
              <ul className="text-sm text-zinc-300 space-y-1">
                <li>✓ All active tradelines with posting dates</li>
                <li>✓ Tier pricing breakdown</li>
                <li>✓ $100 discount for 2+ tradelines</li>
                <li>✓ Payment methods (Zelle, Venmo, Apple Pay)</li>
                <li>✓ 90-120 days active + permanent history info</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailData.client_name || !emailData.client_email}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Box */}
      <div className="bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
        <p className="text-sm text-zinc-300">
          <strong className="text-purple-400">How Trade Lines Work:</strong> Posts on the date listed each month • Remains active for 90-120 days • After removal, account history remains on credit report <strong className="text-green-400">PERMANENTLY</strong> as positive history!
        </p>
      </div>
    </div>
  );
}
